import debounce from 'lodash.debounce';
import {
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormClearErrors,
  type UseFormSetError,
  type UseFormSetValue,
} from 'react-hook-form';
import {
  phoneNumberOptionalRegExp,
  phoneNumberRegExp,
} from '@/utils/validation';

export interface CreatePhoneHandlersParams<
  T extends FieldValues,
  TFieldName extends Path<T> = Path<T>,
> {
  fieldName: TFieldName;
  checkPhoneNumber: (args: { phoneNumber: string }) => Promise<any>;
  clearErrors: UseFormClearErrors<T>;
  setError: UseFormSetError<T>;
  setValue: UseFormSetValue<T>;
  errorMessage: string;
  debounceTime: number;
}

/**
 * Debounced Phone Validation
 *
 * Numbers are validated by the backend (libphonenumber), so every prefix of a
 * not-yet-complete number is genuinely "invalid". Verdicts are therefore held
 * back until the user has finished typing (debounce pause or blur), and a
 * stale API response that resolves out of order can never overwrite the
 * verdict of the newest value (SWI-676).
 */
export function createPhoneHandlers<
  T extends FieldValues,
  TFieldName extends Path<T> = Path<T>,
>({
  fieldName,
  checkPhoneNumber,
  clearErrors,
  setError,
  setValue,
  errorMessage,
  debounceTime,
}: CreatePhoneHandlersParams<T, TFieldName>) {
  // Track whether the field has been blurred at least once so we can
  // revalidate via API on subsequent keystrokes.
  let hasBlurred = false;

  // Newest value handed to the API together with its (possibly settled)
  // promise, so concurrent checks of the same value share one request.
  let latestCheck: { value: string; promise: Promise<boolean> } | null = null;

  // Monotonic id per verdict application: responses can resolve out of
  // order, so only the newest application may write the error state.
  let applySeq = 0;

  // Newest value seen by validatePhoneBeforeSubmit — parked invocations
  // re-validate this instead of their own stale snapshot.
  let newestValidateValue = '';

  // Typing-pause tracking: validators invoked mid-typing park here until
  // debounceTime has elapsed since the last keystroke.
  let lastChangeAt = 0;
  let pauseTimer: ReturnType<typeof setTimeout> | null = null;
  let pauseWaiters: Array<() => void> = [];

  const releasePauseWaiters = () => {
    if (pauseTimer) {
      clearTimeout(pauseTimer);
      pauseTimer = null;
    }
    const waiters = pauseWaiters;
    pauseWaiters = [];
    waiters.forEach((resolve) => resolve());
  };

  const noteTyping = () => {
    lastChangeAt = Date.now();
    if (pauseTimer) {
      clearTimeout(pauseTimer);
    }
    pauseTimer = setTimeout(releasePauseWaiters, debounceTime);
  };

  const waitForTypingPause = (): Promise<void> => {
    const sinceChange = Date.now() - lastChangeAt;
    if (sinceChange >= debounceTime) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      pauseWaiters.push(resolve);
      if (!pauseTimer) {
        pauseTimer = setTimeout(
          releasePauseWaiters,
          debounceTime - sinceChange
        );
      }
    });
  };

  // FormPhoneInput owns the country prefix during typing — the value here is
  // the local part (e.g. "690"), not E.164. Only canonicalise when the user
  // explicitly typed an international form ("+…" or "00…"); for local digits
  // skip the "+"-prepend so we don't poison the controlled value with a bogus
  // prefix (e.g. "690" → "+690"). The value is fully normalised to E.164 on
  // blur by FormPhoneInput's resolveToE164.
  const sanitizePhoneValue = (value: string) => {
    if (value.startsWith('+')) {
      return `+${value.slice(1).replace(/\D/g, '')}`;
    }
    if (value.startsWith('00')) {
      return `+${value.slice(2).replace(/\D/g, '')}`;
    }
    return value.replace(/\D/g, '');
  };

  const checkPhoneValidity = (sanitizedValue: string): Promise<boolean> => {
    // Empty or just a prefix → skip API call, consider valid (optional field)
    if (!sanitizedValue || phoneNumberOptionalRegExp.test(sanitizedValue)) {
      return Promise.resolve(true);
    }
    if (latestCheck?.value !== sanitizedValue) {
      latestCheck = {
        value: sanitizedValue,
        promise: checkPhoneNumber({ phoneNumber: sanitizedValue })
          .then((res) => !!res?.response?.success)
          .catch(() => false),
      };
    }
    return latestCheck.promise;
  };

  // Writes the verdict to the form. Guarded so a stale response resolving
  // after a newer keystroke/blur can never overwrite the newer verdict.
  const applyCheck = (sanitizedValue: string): Promise<void> => {
    const seq = ++applySeq;
    return checkPhoneValidity(sanitizedValue).then((valid) => {
      if (seq !== applySeq) {
        return;
      }
      if (valid) {
        clearErrors(fieldName);
      } else {
        setError(fieldName, { message: errorMessage });
      }
    });
  };

  // Post-blur keystrokes surface their verdict only once the user pauses.
  const debouncedApplyCheck = debounce((sanitizedValue: string) => {
    void applyCheck(sanitizedValue);
  }, debounceTime);

  const debouncedCheckPhone = Object.assign(
    (sanitizedValue: string) => {
      debouncedApplyCheck(sanitizedValue);
    },
    {
      cancel: () => {
        debouncedApplyCheck.cancel();
        releasePauseWaiters();
      },
    }
  );

  // The current value needs no API verdict (empty / prefix-only / local
  // form): retire scheduled and in-flight applications and clear the error.
  const clearVerdict = () => {
    debouncedApplyCheck.cancel();
    applySeq += 1;
    clearErrors(fieldName);
  };

  /**
   * Called on every keystroke. Normalises the raw input to E.164, writes it
   * back to the form via setValue (≥ 3 chars), and – after the first blur –
   * schedules a debounced revalidation. Verdicts are never surfaced
   * mid-typing: any existing error is hidden until the user pauses or blurs.
   */
  const handlePhoneChange = (value: string) => {
    noteTyping();
    const sanitized = sanitizePhoneValue(value);

    // Value is empty or still just an incomplete prefix → clear any error.
    if (!value || phoneNumberOptionalRegExp.test(sanitized)) {
      clearVerdict();
      return;
    }

    // Only write back to RHF when the value is already in canonical "+…"
    // form. For local-form values (e.g. "690") the country prefix is owned
    // by FormPhoneInput's dropdown and will be applied on blur — writing
    // back here would just round-trip the same digits.
    if (sanitized.startsWith('+') && sanitized.length >= 3) {
      setValue(fieldName, sanitized as PathValue<T, TFieldName>, {
        shouldValidate: false,
      });
    }

    // Local-form values are not validatable yet (no country prefix bound).
    // Skip API checks and regex errors — FormPhoneInput will resolve to
    // E.164 on blur and revalidation will kick in then.
    if (!sanitized.startsWith('+')) {
      clearVerdict();
      return;
    }

    // After the first blur we revalidate on every change, but the verdict
    // waits for the debounce pause so it only shows once typing stops.
    if (hasBlurred) {
      applySeq += 1;
      clearErrors(fieldName);
      debouncedCheckPhone(sanitized);
      return;
    }

    // Before first blur: regex-only check to avoid premature error flashing.
    if (!sanitized.startsWith('+0') && !phoneNumberRegExp.test(sanitized)) {
      setError(fieldName, { message: errorMessage });
    } else {
      clearErrors(fieldName);
    }
  };

  /**
   * Called on blur, after FormPhoneInput has already resolved the value to
   * E.164. Blur means the user is done typing: parked validators are
   * released and the verdict applies immediately.
   */
  const handlePhoneBlur = async (value: string) => {
    hasBlurred = true;
    debouncedApplyCheck.cancel();
    releasePauseWaiters();
    const sanitized = sanitizePhoneValue(value);

    if (!value || phoneNumberOptionalRegExp.test(sanitized)) {
      clearVerdict();
      return;
    }

    await applyCheck(sanitized);
  };

  /**
   * RHF `rules.validate` entry point. `mode: 'onChange'` forms invoke this
   * on every keystroke and apply whichever result resolves last, so two
   * guards make that safe: invocations park until typing pauses (one API
   * call for the final value instead of one per keystroke), and every
   * invocation resolves with the verdict of the newest value — a stale
   * response can never flag a number the user has since completed.
   */
  const validatePhoneBeforeSubmit = async (value: string) => {
    const sanitized = sanitizePhoneValue(value);
    newestValidateValue = sanitized;

    if (!sanitized || phoneNumberOptionalRegExp.test(sanitized)) {
      clearErrors(fieldName);
      return true;
    }

    await waitForTypingPause();

    // Typing may have continued while parked — keep following the newest
    // value until it holds still, so all pending invocations resolve with
    // the same, current verdict.
    let valid = true;
    let target = '';
    do {
      target = newestValidateValue;
      if (!target || phoneNumberOptionalRegExp.test(target)) {
        valid = true;
        break;
      }
      valid = await checkPhoneValidity(target);
    } while (newestValidateValue !== target);

    if (valid) {
      clearErrors(fieldName);
      return true;
    }

    return errorMessage;
  };

  return {
    handlePhoneChange,
    handlePhoneBlur,
    validatePhoneBeforeSubmit,
    debouncedCheckPhone,
  };
}
